import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import cx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Disclaimer, FormattedMoney, MONEY_TOKENS } from '@repo/design-system-kit';
import CURRENCIES from 'receivable-invoices-js/constants/currencies';
import type { CreditNote } from 'receivable-invoices-js/react/api/models/credit-note';
import styles from './information.module.css';

interface CreditNotesInformationProps extends ComponentPropsWithoutRef<'div'> {
  creditNote: CreditNote;
}

export function CreditNotesInformation({
  creditNote,
  className,
  ...props
}: CreditNotesInformationProps): ReactNode {
  const currency = creditNote.currency ?? CURRENCIES.default;

  return (
    <div className={className} {...props}>
      <p className="title-3 mb-16" data-test-credit-note-information-number>
        <FormattedMessage
          id="credit-note.preview.credit-note-number"
          values={{ creditNoteNumber: creditNote.number }}
        />
      </p>
      <div className={styles.price} data-test-credit-note-information-price>
        <h1 className={cx(styles.priceValue, 'title-1')}>
          <FormattedMoney
            amount={creditNote.amountDue.value}
            currency={currency}
            token={MONEY_TOKENS.ISO_SIGN_DECIMAL}
          />
        </h1>
        <span className={cx(styles.priceVat, 'caption')}>
          <FormattedMessage id="credit-note.preview.include-vat" />
        </span>
      </div>
      <p className="title-4 mb-16" data-test-credit-note-information-sent-by>
        <FormattedMessage
          id="credit-note.preview.sent-by"
          values={{ companyName: creditNote.organization.legalName }}
        />
      </p>
      <Disclaimer.Inline data-test-credit-note-information-disclaimer="" level="info">
        <FormattedMessage
          id="credit-note.preview.disclaimer"
          values={{
            creditNoteAmount: (
              <FormattedMoney
                amount={creditNote.amountDue.value}
                currency={currency}
                token={MONEY_TOKENS.ISO_SIGN_DECIMAL}
              />
            ),
            invoiceNumber: creditNote.originalInvoice.number,
          }}
        />
      </Disclaimer.Inline>
    </div>
  );
}
